/**
 * @fileoverview FiledByGrowLine: the one row that says Grow filed a photo onto a plant by itself, with the way to move it (B160).
 * The photo's thumbnail (when the server gave one), the sentence ("Filed to Sweet Horizon peas (Front bed)"), and a Move link, in
 * one row at least 44px tall. The sentence is the server's (todo_word tray.filed.to), drawn as sent; Move is the app's word.
 * `tone="onPhoto"` is the same line laid over a photo's dark fade (the plant card): it takes the colour of the place it sits in.
 *
 * Where: the Photos page's "Filed by Grow" strip (FiledStrip), the toast right after adding a photo (sayFiled), the plant card's
 * photo (PlantCardPhoto).
 * API: none.
 * Words (en/es): tray.filed.move.
 *
 * @module FiledByGrowLine
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** What the line is given. */
export interface FiledByGrowLineProps {
  /** The sentence, as the server (or the card's own word) says it. */
  text: string;
  /** The photo's thumbnail address; no picture is drawn without one. */
  thumbUrl?: string;
  /** Called when Move is tapped. */
  onMove: () => void;
  /** 'plain' on the page's own ground; 'onPhoto' over a photo, in the colour of the place it sits in. */
  tone?: 'plain' | 'onPhoto';
  /** More classes for the row. */
  className?: string;
}

/**
 * The "Filed by Grow" row: thumbnail, sentence, Move.
 *
 * @param props - FiledByGrowLineProps.
 */
export const FiledByGrowLine: React.FC<FiledByGrowLineProps> = ({ text, thumbUrl, onMove, tone = 'plain', className }) => {
  const { reactTranslate: t } = useTranslation();
  const onPhoto = tone === 'onPhoto';
  return (
    <div className={cn('flex w-full items-center gap-2', onPhoto ? 'min-h-9 text-xs' : 'min-h-11 text-sm', className)}>
      {thumbUrl && (
        <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-md bg-muted">
          <FramedPhoto src={thumbUrl} alt="" loading="lazy" draggable={false} />
        </span>
      )}
      <span className="min-w-0 grow">{text}</span>
      <Button
        type="button"
        variant="link"
        size="sm"
        className={cn('shrink-0 font-semibold', onPhoto && 'text-inherit')}
        onClick={(event) => { event.stopPropagation(); onMove(); }}
      >
        {t('tray.filed.move')}
      </Button>
    </div>
  );
};
